/**
 * JD readiness check — shared, framework-agnostic (PD-1250).
 *
 * Turns the raw per-factor output of the "JD readiness" prompt chain into a
 * per-factor / overall coverage assessment, so rm-portal and rm-portal-beta
 * present IDENTICAL results. No Vue, no API — pure functions over the plain
 * framework data returned by document/loadDocumentLfEvaluation (factors[] with
 * subFactors[], each carrying id / name / description / weighting) and the
 * chain's `sub_factor_evidence` response.
 *
 * The scoring rubric mirrors the "Approach A" prompt spec:
 *   · item_score      = min(alignment_score, centrality_score) / 5   (both axes
 *                       must be strong; a Direct-but-Peripheral mention is weak)
 *   · sub-factor      = max(item_score) across its evidence (best sentence wins;
 *                       0 when it has no evidence → not_detected)
 *   · factor          = weighting-weighted average of its sub-factors
 *   · overall (0-100) = Σ(effective_weight · quality) / Σ(effective_weight),
 *                       effective_weight = factor.weighting · sub_factor.weighting,
 *                       errored sub-factors dropped from both sums (a broken call
 *                       is NOT "no evidence").
 *
 * Coverage bands (tunable via the ai_assist_chain_ids client setting):
 *   · sufficient (green) — quality >= greenMin
 *   · thin       (amber) — 0 < quality < greenMin
 *   · missing    (red)   — quality === 0
 */

const n = (v) => Number(v) || 0;

export const DEFAULT_READINESS_THRESHOLDS = {
  // Coverage band cutoff: a factor reads "Sufficient" at or above this quality.
  green_min: 0.6,
};

/* ---- prompt input helpers ------------------------------------------------- */

/**
 * One line per sub-factor, in the exact format the prompt expects:
 *   - id={sub_factor_id}: {name} — {description}
 * `subFactors` = the factor's own list ({ id, name, description }).
 */
export function buildSubFactorsBlock(subFactors) {
  return (subFactors || [])
    .map((sf) => `- id=${sf.id}: ${sf.name} — ${sf.description || ''}`)
    .join('\n');
}

/* ---- consolidation -------------------------------------------------------- */

/**
 * One factor's raw chain response → per-sub-factor results + factor rollup.
 * `subFactors` = this factor's list ({ id, name, weighting }).
 * `response`   = the parsed { sub_factor_evidence: [...] } object (or null when
 *                the call errored, which flags every sub-factor as `error`).
 */
export function consolidateFactor(subFactors, response) {
  const errored = !response || !Array.isArray(response.sub_factor_evidence);

  const evidenceById = {};
  if (!errored) {
    for (const group of response.sub_factor_evidence) {
      // ids come back as strings; key on String() so lookups are type-safe.
      evidenceById[String(group.sub_factor_id)] = group.evidence || [];
    }
  }

  const subFactorResults = (subFactors || []).map((sf) => {
    if (errored) {
      return {
        sub_factor_id: sf.id,
        name: sf.name,
        weighting: n(sf.weighting),
        evidence_quality: 0,
        status: 'error',
        evidence: [],
      };
    }

    const evidence = evidenceById[String(sf.id)] || [];
    const itemScores = evidence.map(
      (item) => Math.min(n(item.alignment_score), n(item.centrality_score)) / 5
    );
    const quality = itemScores.length ? Math.max(...itemScores) : 0;

    return {
      sub_factor_id: sf.id,
      name: sf.name,
      weighting: n(sf.weighting),
      evidence_quality: quality,
      status: quality > 0 ? 'evidence_available' : 'not_detected',
      evidence,
    };
  });

  // Factor rollup: weighting-weighted average of its own sub-factors. When the
  // call errored there is no evidence to roll up — surface it as `error`.
  const factorQuality = errored
    ? 0
    : subFactorResults.reduce((sum, sf) => sum + sf.weighting * sf.evidence_quality, 0);

  return {
    evidence_quality: factorQuality,
    status: errored ? 'error' : factorQuality > 0 ? 'evidence_available' : 'not_detected',
    sub_factors: subFactorResults,
  };
}

/**
 * All factors' consolidated results → one overall 0-100 readiness score.
 * `allFactors` = [{ weighting, sub_factors: [{ weighting, evidence_quality, status }] }].
 * Returns null when every contributing sub-factor errored (nothing to score).
 */
export function overallReadinessScore(allFactors) {
  let totalWeight = 0;
  let weightedSum = 0;

  for (const factor of allFactors || []) {
    for (const sf of factor.sub_factors || []) {
      if (sf.status === 'error') continue; // skip, don't treat as zero
      const effectiveWeight = n(factor.weighting) * n(sf.weighting);
      if (effectiveWeight === 0) continue; // contextual (zero-weight) — advisory only
      totalWeight += effectiveWeight;
      weightedSum += effectiveWeight * sf.evidence_quality;
    }
  }

  if (totalWeight === 0) return null;
  return 100 * (weightedSum / totalWeight);
}

/* ---- presentation helpers ------------------------------------------------- */

/**
 * Map a factor's consolidated evidence_quality to a coverage band for the UI.
 * `status` is passed so an errored factor can render distinctly from "missing".
 */
export function factorCoverage(quality, status, thresholds) {
  const greenMin = n((thresholds || DEFAULT_READINESS_THRESHOLDS).green_min)
    || DEFAULT_READINESS_THRESHOLDS.green_min;

  if (status === 'error') {
    return { level: 'error', colour: '#9e9e9e', label: 'Not checked' };
  }
  if (quality >= greenMin) {
    return { level: 'sufficient', colour: '#4caf50', label: 'Sufficient' };
  }
  if (quality > 0) {
    return { level: 'thin', colour: '#ffa726', label: 'Thin' };
  }
  return { level: 'missing', colour: '#ef5350', label: 'Missing' };
}

/**
 * Display weight for a factor row: a percentage for weighted factors,
 * "Contextual" for zero-weight factors (evaluated for coverage but excluded
 * from the overall weighted score).
 */
export function weightLabel(weighting) {
  const w = n(weighting);
  return w > 0 ? `${Math.round(w * 100)}%` : 'Contextual';
}

/**
 * One-line summary shown in the panel header, derived from the weighted
 * factors' coverage. `factorResults` = [{ weighting, status }] (factor rollups).
 */
export function readinessSummary(factorResults) {
  const weighted = (factorResults || []).filter((f) => n(f.weighting) > 0);

  if (!weighted.length) {
    return 'The document has been checked against your framework.';
  }
  if (weighted.every((f) => f.status === 'error')) {
    return 'The readiness check could not be completed. You can still proceed.';
  }

  const missing = weighted.filter((f) => f.status === 'not_detected').length;
  const thin = weighted.filter(
    (f) => f.status === 'evidence_available' && n(f.evidence_quality) < DEFAULT_READINESS_THRESHOLDS.green_min
  ).length;

  if (missing === 0 && thin === 0) {
    return 'This document looks ready to evaluate. Evidence found for every weighted factor group.';
  }
  if (missing > 0) {
    return `Evidence is missing for ${missing} weighted factor group${missing === 1 ? '' : 's'}. You can still proceed — the check is advisory.`;
  }
  return `Evidence is thin for ${thin} weighted factor group${thin === 1 ? '' : 's'}. You can still proceed — the check is advisory.`;
}
